import React, { useEffect, useState } from 'react';
import { Modal, TextInput, Button, Stack, Group } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { useTranslation } from 'react-i18next';
import { updateCompany } from '@mygpt/app';
import { useAdminStore } from '@/store/admin';

interface EditCompanyModalProps {
  opened: boolean;
  onClose: () => void;
  /** 更新成功后的回调，用于刷新页面上的企业名称 */
  onUpdated: () => Promise<void> | void;
}

/**
 * 编辑企业名称弹窗
 * 仅公司所有者和公司管理员可用；描述和初始密码沿用现有值原样回传，不在此处修改
 */
export const EditCompanyModal: React.FC<EditCompanyModalProps> = ({ opened, onClose, onUpdated }) => {
  const { t } = useTranslation();
  const { companyId, companyName, companyDescription, companySetting, fetchCompanySetting } = useAdminStore();
  const [name, setName] = useState('');
  const [loading, setLoading] = useState(false);

  // 打开弹窗时回填当前企业名称
  useEffect(() => {
    if (!opened) return;
    setName(companyName || '');
  }, [opened, companyName]);

  // 确保已加载 companySetting（初始密码需原样回传），与表单回填分开，避免加载完成后覆盖用户输入
  useEffect(() => {
    if (!opened || companySetting || !companyId) return;
    fetchCompanySetting({ companyId }).catch((error: unknown) => {
      console.error('加载企业设置失败:', error);
    });
  }, [opened, companyId, companySetting, fetchCompanySetting]);

  const handleSubmit = async () => {
    const trimmedName = name.trim();
    if (!trimmedName || !companyId) return;

    setLoading(true);
    try {
      const { data } = await updateCompany({
        id: companyId,
        name: trimmedName,
        description: companyDescription || '',
        initialPassword: companySetting?.initialPassword || '',
      });
      if (!data?.success) {
        throw new Error(data?.error?.name || 'UPDATE_COMPANY_FAILED');
      }
      notifications.show({ message: t('adminConsole.tip.update_success'), color: 'green' });
      onClose();
      await onUpdated();
    } catch (error) {
      console.error('更新企业信息失败:', error);
      notifications.show({ message: t('adminConsole.tip.update_error'), color: 'red' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal opened={opened} onClose={onClose} title={t('adminConsole.companyModal.title')} centered>
      <Stack>
        <TextInput
          label={t('adminConsole.companyModal.name')}
          value={name}
          onChange={(e) => setName(e.currentTarget.value)}
          required
          maxLength={100}
          data-autofocus
        />
        <Group justify="flex-end" mt="md">
          <Button variant="outline" onClick={onClose} disabled={loading}>
            {t('common.cancel')}
          </Button>
          <Button onClick={handleSubmit} loading={loading} disabled={!name.trim()}>
            {t('common.saveChanges')}
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
};
